import dayjs, { Dayjs } from 'dayjs';
import { fetchCouriersByRegion } from 'fetch/fetchCouriers';
import { useEffect, useState } from 'react';
import { CourierJson } from 'types/bundleTypes';
import { Region } from 'types/orderTypes';
import {
  PusherEvent,
  subscribeToChannel,
  unsubscribeFromChannel,
} from 'utils/pusher';
import { fetchShifts, IShift, ShiftExtraWork } from './shiftApi';
import {
  CourierCollection,
  handleError,
  JobType,
  SHIFT_PLANNER_CHANNEL,
  ShiftCollection,
  ShiftCount,
  ShiftsByDate,
  ShiftsById,
} from './utils';
import { ReqEnum } from '../../const/requestStatus';
import { ShiftStatusType } from '../../const/ShiftConst';

const useLiveShifts = (
  startDate: Dayjs,
  endDate: Dayjs,
  region: Region
): { allShifts: ShiftsById; fetchStatus: ReqEnum } => {
  const [allShifts, setAllShifts] = useState<ShiftsById>({});
  const [fetchStatus, setFetchStatus] = useState<ReqEnum>(ReqEnum.INIT);

  useEffect(() => {
    (async () => {
      // querying for 2 weeks.
      // One to display, the second one for the "x shifts last week" stat
      setFetchStatus(ReqEnum.PENDING);
      try {
        const shiftsList = await fetchShifts(
          region,
          startDate.subtract(1, 'week'),
          endDate
        );

        const shiftsById: ShiftsById = {};
        shiftsList.forEach(shift => (shiftsById[shift.id] = shift));
        setAllShifts(shiftsById);
        setFetchStatus(ReqEnum.SUCCESS);
      } catch (error) {
        handleError(error);
        setFetchStatus(ReqEnum.ERROR);
      }
    })();
  }, [startDate, endDate, region]);

  useEffect(() => {
    subscribeToChannel(
      SHIFT_PLANNER_CHANNEL,
      PusherEvent.SHIFT_PLANNER_CREATE,
      (newShifts: IShift[]) => {
        setAllShifts(oldState => {
          const oldStateCopy = { ...oldState };
          newShifts.forEach(newShift => {
            const shiftDate = dayjs(new Date(newShift.date));
            if (
              shiftDate.isBetween(startDate, endDate, 'day', '[]') &&
              newShift.region == region
            ) {
              oldStateCopy[newShift.id] = newShift;
            }
          });
          return oldStateCopy;
        });
      }
    );

    subscribeToChannel(
      SHIFT_PLANNER_CHANNEL,
      PusherEvent.SHIFT_PLANNER_UPDATE,
      (updatedShift: IShift) => {
        (async () => {
          const shiftDate = dayjs(new Date(updatedShift.date));
          if (shiftDate.isBetween(startDate, endDate, 'day', '[]')) {
            setAllShifts(oldState => {
              const oldStateCopy = { ...oldState };
              oldStateCopy[updatedShift.id] = updatedShift;
              return oldStateCopy;
            });
          }
        })();
      }
    );

    subscribeToChannel(
      SHIFT_PLANNER_CHANNEL,
      PusherEvent.SHIFT_PLANNER_EXTRA_WORK_UPDATE,
      ({ updatedShifts }: { updatedShifts: ShiftExtraWork[] }) => {
        setAllShifts(oldState => {
          const oldStateCopy = { ...oldState };
          updatedShifts.forEach(shift => {
            if (oldStateCopy[shift.shiftId]) {
              oldStateCopy[shift.shiftId].extraWork = shift.extraWork;
            }
          });
          return oldStateCopy;
        });
      }
    );

    subscribeToChannel(
      SHIFT_PLANNER_CHANNEL,
      PusherEvent.SHIFT_PLANNER_DELETE,
      (shiftIds: string[]) =>
        setAllShifts(oldState => {
          const oldStateCopy = { ...oldState };
          shiftIds.forEach(id => delete oldStateCopy[id]);
          return oldStateCopy;
        })
    );

    subscribeToChannel(
      SHIFT_PLANNER_CHANNEL,
      PusherEvent.SHIFT_PLANNER_PUBLISH,
      ({ shiftIds }: { shiftIds: string[] }) =>
        setAllShifts(oldState => {
          const oldStateCopy = { ...oldState };
          Object.keys(oldStateCopy)
            .filter(shiftId => shiftIds.includes(shiftId))
            .forEach(shiftId => {
              oldStateCopy[shiftId].published = true;
            });
          return oldStateCopy;
        })
    );

    return () => unsubscribeFromChannel(SHIFT_PLANNER_CHANNEL);
  }, [startDate, endDate]);

  return { allShifts, fetchStatus };
};

const useCouriersList = (
  displayedJobType: JobType,
  region: Region
): CourierCollection => {
  const [sorters, setSorters] = useState<CourierJson[]>([]);
  const [couriers, setCouriers] = useState<CourierJson[]>([]);
  const [displayedCouriers, setDisplayedCouriers] = useState<CourierCollection>(
    {
      list: [],
      byId: {},
    }
  );

  const setFilteredCouriers = (filteredCouriers: CourierJson[]) => {
    const couriersById = {};
    filteredCouriers.forEach(courier => (couriersById[courier.id] = courier));

    setDisplayedCouriers({
      list: filteredCouriers,
      byId: couriersById,
    });
  };

  useEffect(() => {
    (async () => {
      const allCouriers = await fetchCouriersByRegion(region);

      setCouriers(allCouriers.filter(courier => courier.isCourier));
      setSorters(allCouriers.filter(courier => courier.isSorter));
    })();
  }, [region]);

  useEffect(() => {
    switch (displayedJobType) {
      case JobType.ALL:
        setFilteredCouriers([...new Set([...couriers, ...sorters])]);
        break;
      case JobType.COURIER:
        setFilteredCouriers(couriers);
        break;
      case JobType.SORTER:
        setFilteredCouriers(sorters);
        break;
      default:
        throw new Error('No jobtype selected');
    }
  }, [couriers, sorters, displayedJobType]);

  return displayedCouriers;
};

export const useShiftCouriers = (
  startDate: Dayjs,
  endDate: Dayjs,
  displayedJobType: JobType,
  onlyExtraWork: boolean,
  region: Region
): {
  displayedShifts: ShiftCollection;
  displayedCouriers: CourierCollection;
  fetchStatus: ReqEnum;
} => {
  const { allShifts, fetchStatus } = useLiveShifts(startDate, endDate, region);
  const displayedCouriers = useCouriersList(displayedJobType, region);

  const [filteredShifts, setFilteredShifts] = useState<ShiftsById>({});
  const [displayedShifts, setDisplayedShifts] = useState<ShiftCollection>({
    byDate: {},
    currentShifts: [],
    shiftCount: { previousWeek: {}, currentWeek: {} },
  });

  useEffect(() => {
    // Apply filter to the shifts

    // Default is to include all shifts
    const _filteredShifts = { ...allShifts };

    // Filters are based on courier type, so remove shifts with whichever
    // couriers we dont want to show.
    if (displayedJobType !== JobType.ALL) {
      const courierIds = Object.keys(displayedCouriers.byId);

      // Remove shifts that are not included in displayedCouriers
      Object.entries(_filteredShifts).forEach(([shiftId, shift]) => {
        if (!courierIds.includes(shift.courierId?.toString())) {
          delete _filteredShifts[shiftId];
        }
      });
    }

    // Remove shifts without extra work, if the option is selected.
    if (onlyExtraWork) {
      Object.entries(_filteredShifts).forEach(([shiftId, shift]) => {
        if (shift.extraWork.length === 0) {
          delete _filteredShifts[shiftId];
        }
      });
    }

    setFilteredShifts(_filteredShifts);
  }, [allShifts, displayedCouriers, displayedJobType, onlyExtraWork]);

  useEffect(() => {
    // After shifts are filtered, prepare the necessary data for the
    // rest of the shift planner.
    const shiftsByDate: ShiftsByDate = {};
    Object.values(filteredShifts).forEach(shift => {
      if (shiftsByDate[shift.date] == null) {
        shiftsByDate[shift.date] = [];
      }

      shiftsByDate[shift.date].push(shift);
    });

    const currentShifts: IShift[] = [];
    const shiftCount: ShiftCount = { previousWeek: {}, currentWeek: {} };
    Object.values(filteredShifts).forEach(shift => {
      let week: string;
      if (dayjs(new Date(shift.date)).isBefore(startDate, 'day')) {
        week = 'previousWeek';
      } else {
        week = 'currentWeek';
        currentShifts.push(shift);
      }

      // Checking courierId should be unnecessary if the status is
      // booked, but why not...
      if (shiftCount[week][shift.courierId] == null) {
        shiftCount[week][shift.courierId] = {
          booked: 0,
          requested: 0,
          declined: 0,
          dispatchDeclined: 0,
          canceled: 0,
        };
      }

      if (shift.courierId) {
        switch (shift.status) {
          case ShiftStatusType.BOOKED:
            shiftCount[week][shift.courierId].booked++;
            break;
          case ShiftStatusType.REQUESTED:
            shiftCount[week][shift.courierId].requested++;
            break;
          case ShiftStatusType.DECLINED:
            shiftCount[week][shift.courierId].declined++;
            break;
          case ShiftStatusType.CANCELED:
            shiftCount[week][shift.courierId].canceled++;
            break;
          case ShiftStatusType.DISPATCH_DECLINED:
            shiftCount[week][shift.courierId].dispatchDeclined++;
            break;
        }
      }
    });

    setDisplayedShifts({ byDate: shiftsByDate, currentShifts, shiftCount });
  }, [filteredShifts, startDate]);

  return { displayedShifts, displayedCouriers, fetchStatus };
};
